import { Moment } from "moment";
import { GeneratorParams, COLUMN_ID, COLUMN_TYPE } from ".";

export default function generate({
    statuses,
    names,
    creationTimes,
    itemSpeedFactor,
    random,
    minStatusT,
    statusDt,
    skipStatusChance,
    movePastLastStatus,
    closedStatus: doneStatus,
    chanceOfJumpingToClosedStatus,
    chanceOfStayingInStatus
}: GeneratorParams): {
    activityLogs: {
        created_at: number;
        dataJson: string;
        event: "update_column_value";
    }[];
    lastStatusPerItem: { [item: string]: number };
} {
    // dt range per status - actual values will be chosen from range [min, max] per status
    const statusDtMin: { [labelId: string]: number } = {}; // min time ticket can spend in status
    const statusDtMax: { [labelId: string]: number } = {}; // max time ticket can spend in status
    for (const s in statuses) {
        const labelId = `${s}`;
        // generate data for each task - traverse all statuses
        statusDtMin[labelId] = Math.floor(random() * (Array.isArray(minStatusT) ? minStatusT[s] : minStatusT));
        statusDtMax[labelId] = Math.floor(random() * (Array.isArray(statusDt) ? statusDt[s] : statusDt));
    }

    // array of created_at times for each status for each item
    const transitions: { [id: string]: Moment[] } = {};
    for (const n in names) {
        const itemId = `${n}`;
        transitions[itemId] = [];
        const currentTime: Moment = creationTimes[n].clone();
        for (let s = 0; s < statuses.length; s++) {
            currentTime.add(itemSpeedFactor[n] * (statusDtMin[s] + Math.floor(random() * statusDtMax[s])), "ms");
            transitions[itemId].push(currentTime.clone());
        }
    }

    const closedStatusId = statuses.findIndex((s) => s === doneStatus);

    type LogEntry = { created_at: number; dataJson: any; event: "update_column_value" };

    const logEntry = (name: string, status: number, prev_index: number, created_at: Moment): LogEntry => ({
        created_at: created_at.valueOf() * 1e4,
        dataJson: {
            pulse_id: name,
            column_id: COLUMN_ID,
            column_type: COLUMN_TYPE,
            value: { label: { index: status } },
            previous_value: { label: { index: prev_index } }
        },
        event: "update_column_value"
    });

    const logs: LogEntry[] = [];
    const lastStatusPerItem: { [item: string]: number } = {};
    for (const n in names) {
        let prevIndex = 0;
        for (let s = 1; s < statuses.length; s++) {
            // you cannot skip the first status (items always start in first status)
            if (random() < skipStatusChance[s]) {
                continue;
            }
            if (closedStatusId >= 0 && chanceOfJumpingToClosedStatus && random() < chanceOfJumpingToClosedStatus[s]) {
                logs.push(logEntry(n, closedStatusId, prevIndex, transitions[`${n}`][s - 1]));
                prevIndex = closedStatusId;
                break;
            }
            if (chanceOfStayingInStatus && random() < chanceOfStayingInStatus[s - 1]) {
                break;
            }
            logs.push(logEntry(n, s, prevIndex, transitions[`${n}`][s - 1]));
            prevIndex = s;
        }
        // optionally move somewhere else after done status, if item not closed
        if (movePastLastStatus && prevIndex !== closedStatusId) {
            let lastStatus;
            // prevent choosing the same status
            do {
                lastStatus = Math.round((statuses.length - 1) * random());
            } while (lastStatus === prevIndex && statuses.length > 1);
            logs.push(logEntry(n, lastStatus, prevIndex, transitions[`${n}`][statuses.length - 1]));
            prevIndex = lastStatus;
        }
        lastStatusPerItem[n] = prevIndex;
    }

    // sort in desc order
    return { activityLogs: logs.sort((a, b) => b.created_at - a.created_at), lastStatusPerItem };
}

/*
export  [
    {
        created_at: "15977795625174580",
        data:
            '{"board_id":653447291,"group_id":"topics","is_top_group":true,"pulse_id":672156039,"pulse_name":"test 2a","column_id":"status","column_type":"color","column_title":"Status","value":{"label":{"index":3,"text":"Design","style":{"color":"#0086c0","border":"#3DB0DF","var_name":"blue-links"}},"post_id":null},"previous_value":{"label":{"index":1,"text":"Done","style":{"color":"#00c875","border":"#00B461","var_name":"green-shadow"}},"post_id":null},"is_column_with_hide_permissions":false}',
        event: "update_column_value"
    },
    {
        created_at: "15977794778314946",
        data:
            '{"board_id":653447291,"group_id":"topics","is_top_group":true,"pulse_id":672156381,"pulse_name":"test 6","column_id":"status","column_type":"color","column_title":"Status","value":{"label":{"index":2,"text":"Stuck","style":{"color":"#FF642E","border":"#E05828","var_name":"dark-orange"}},"post_id":null},"previous_value":{"label":{"index":3,"text":"Design","style":{"color":"#0086c0","border":"#3DB0DF","var_name":"blue-links"}},"post_id":null},"is_column_with_hide_permissions":false}',
        event: "update_column_value"
    },
    ...
]
*/
